import { useState } from 'react'
import { useChangeIntranetMeal, useIntranetMeals, useSaveIntranetMeal } from '../api/queries'
import type { IntranetMeal } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { useCreateRequest } from '../lib/createRequest'
import { formatDateTime } from '../lib/format'
import { Avatar, Card, EmptyState, ErrorBox, Loading } from './ui'

/**
 * Fanen "Frokost" (docs/DECISIONS.md beslutning 124): fælles måltider, som kontoret opretter - julefrokost,
 * fredagsmorgenbrød. Alle melder sig til med evt. særlige behov, og den første tager tjansen.
 */

const errorText = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback)

/** ISO -> værdien til <input type="datetime-local"> i browserens (danske) tid. */
function toLocalInput(iso: string) {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function MealsTab() {
  const { data, isLoading, error } = useIntranetMeals()
  const { isIntranetAdmin } = useAuth()
  const [editing, setEditing] = useState<IntranetMeal | 'new' | null>(null)

  useCreateRequest(['maaltid'], () => {
    if (isIntranetAdmin) setEditing('new')
  })

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  const upcoming = (data ?? []).filter((m) => new Date(m.servedAt) > new Date())
  const past = (data ?? []).filter((m) => new Date(m.servedAt) <= new Date())

  return (
    <div className="stack" style={{ gap: 16 }}>
      {isIntranetAdmin && editing === null && (
        <div>
          <button className="btn btn--primary" onClick={() => setEditing('new')}>🍽 Nyt fælles måltid</button>
        </div>
      )}
      {editing !== null && <MealForm meal={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}

      {upcoming.length === 0 && <EmptyState>Der er ingen fælles måltider på vej.</EmptyState>}
      {upcoming.map((meal) => (
        <MealCard key={meal.id} meal={meal} onEdit={isIntranetAdmin ? () => setEditing(meal) : undefined} />
      ))}

      {past.length > 0 && (
        <>
          <div className="small muted">Overståede</div>
          {past.map((meal) => <MealCard key={meal.id} meal={meal} />)}
        </>
      )}
    </div>
  )
}

function MealCard({ meal, onEdit }: { meal: IntranetMeal; onEdit?: () => void }) {
  const { user: me } = useAuth()
  const change = useChangeIntranetMeal()
  const [needs, setNeeds] = useState(meal.myNeeds ?? '')
  const [error, setError] = useState<string | null>(null)
  const past = new Date(meal.servedAt) <= new Date()

  async function act(action: 'signup' | 'signoff' | 'take' | 'release' | 'cancel') {
    if (action === 'cancel' && !window.confirm(`Aflys ${meal.title}? De tilmeldte får besked.`)) return
    setError(null)
    try {
      await change.mutateAsync({ id: meal.id, action, needs: needs.trim() })
    } catch (err) {
      setError(errorText(err, 'Det kunne ikke gemmes.'))
    }
  }

  return (
    <Card title={`🍽 ${meal.title}`}>
      <div className="stack" style={{ gap: 10, opacity: meal.isCancelled || past ? 0.65 : 1 }}>
        <div className="small">
          <strong>{formatDateTime(meal.servedAt)}</strong>
          {meal.isCancelled ? (
            <span className="pill" style={{ marginLeft: 8, background: '#DC2626', color: '#fff' }}>Aflyst</span>
          ) : meal.isOpen ? (
            <span className="muted"> · tilmelding senest {formatDateTime(meal.closesAt)}</span>
          ) : (
            <span className="muted"> · tilmeldingen er lukket</span>
          )}
        </div>
        {meal.description && <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{meal.description}</div>}
        <div className="small">
          {meal.count === 0 ? 'Ingen har meldt sig til endnu.' : `${meal.count} kommer.`}
          {meal.iAmComing && <strong style={{ color: '#16A34A' }}> Du kommer{meal.myNeeds ? ` (${meal.myNeeds})` : ''}.</strong>}
        </div>

        {meal.isOpen && (
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <input
              style={{ maxWidth: 220 }}
              maxLength={100}
              placeholder="Særlige behov, fx vegetar"
              value={needs}
              onChange={(e) => setNeeds(e.target.value)}
            />
            <button className="btn btn--sm btn--primary" disabled={change.isPending} onClick={() => void act('signup')}>
              {meal.iAmComing ? 'Gem' : 'Jeg kommer'}
            </button>
            {meal.iAmComing && (
              <button className="btn btn--sm" disabled={change.isPending} onClick={() => void act('signoff')}>Meld fra</button>
            )}
          </div>
        )}

        {meal.choreText && !meal.isCancelled && (
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', padding: 8, background: '#F8FAFC', borderRadius: 8 }}>
            <span className="small">🧺 {meal.choreText}:</span>
            {meal.choreUser ? (
              <>
                <Avatar user={meal.choreUser} size={24} />
                <strong className="small">{meal.choreUser.id === me?.id ? 'Du tager den' : `${meal.choreUser.displayName} tager den`}</strong>
                {!past && (meal.choreUser.id === me?.id || onEdit) && (
                  <button className="btn btn--sm" disabled={change.isPending} onClick={() => void act('release')}>Giv den fra dig</button>
                )}
              </>
            ) : (
              !past && <button className="btn btn--sm" disabled={change.isPending} onClick={() => void act('take')}>Jeg tager den</button>
            )}
          </div>
        )}

        {meal.signups && meal.signups.length > 0 && (
          <details>
            <summary className="small">{meal.summary}</summary>
            <div className="stack" style={{ gap: 4, marginTop: 6 }}>
              {meal.signups.map((s) => (
                <div key={s.user.id} className="row" style={{ gap: 8 }}>
                  <Avatar user={s.user} size={22} />
                  <span className="small">{s.user.displayName}</span>
                  {s.needs && <span className="pill">{s.needs}</span>}
                </div>
              ))}
            </div>
          </details>
        )}

        {onEdit && !meal.isCancelled && !past && (
          <div className="btn-group">
            <button className="btn btn--sm" onClick={onEdit}>Ret</button>
            <button className="btn btn--sm" disabled={change.isPending} onClick={() => void act('cancel')}>Aflys</button>
          </div>
        )}
        {error && <div className="error-box">{error}</div>}
      </div>
    </Card>
  )
}

function MealForm({ meal, onClose }: { meal: IntranetMeal | null; onClose: () => void }) {
  const save = useSaveIntranetMeal()
  const [title, setTitle] = useState(meal?.title ?? '')
  const [description, setDescription] = useState(meal?.description ?? '')
  const [servedAt, setServedAt] = useState(meal ? toLocalInput(meal.servedAt) : '')
  const [closesAt, setClosesAt] = useState(meal ? toLocalInput(meal.closesAt) : '')
  const [chore, setChore] = useState(meal?.choreText ?? '')
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    setError(null)
    try {
      await save.mutateAsync({
        id: meal?.id,
        title: title.trim(),
        description: description.trim(),
        servedAt: new Date(servedAt).toISOString(),
        closesAt: new Date(closesAt).toISOString(),
        choreText: chore.trim()
      })
      onClose()
    } catch (err) {
      setError(errorText(err, 'Måltidet kunne ikke gemmes.'))
    }
  }

  return (
    <Card title={meal ? `Ret ${meal.title}` : '🍽 Nyt fælles måltid'}>
      <div className="stack" style={{ gap: 10 }}>
        <label className="field">
          <span className="field__label">Hvad er det? *</span>
          <input maxLength={120} placeholder="F.eks. Julefrokost eller Fredagsmorgenbrød" value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Hvornår *</span>
            <input type="datetime-local" value={servedAt} onChange={(e) => setServedAt(e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Tilmelding senest *</span>
            <input type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} />
          </label>
        </div>
        <label className="field">
          <span className="field__label">Beskrivelse</span>
          <textarea rows={2} maxLength={500} placeholder="F.eks. Vi spiser i kantinen. Tag gerne en ven med." value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">Tjans (frivillig - den første tager den)</span>
          <input maxLength={120} placeholder="F.eks. Hent morgenbrød hos bageren" value={chore} onChange={(e) => setChore(e.target.value)} />
        </label>
        {!meal && <div className="tiny muted">Alle får en besked. Når tilmeldingen lukker, får du antal og særlige behov.</div>}
        {error && <div className="error-box">{error}</div>}
        <div className="btn-group">
          <button className="btn btn--primary" disabled={!title.trim() || !servedAt || !closesAt || save.isPending} onClick={() => void submit()}>
            {save.isPending ? 'Gemmer ...' : meal ? 'Gem' : 'Opret'}
          </button>
          <button className="btn" onClick={onClose}>Annullér</button>
        </div>
      </div>
    </Card>
  )
}
